import { logEvent } from 'firebase/analytics';
import { initAnalytics } from '@/firebase/init';
import type { ErrorType } from '@/Pages/Error/types/errorLocationState';

export type WelcomeAnalyticsEvent =
  | { name: 'welcome_page_view' }
  | { name: 'welcome_cta_click'; params?: { destination: string } };

export type MobileAnalyticsEvent = { name: 'mobile_page_view' };

export type TermsAnalyticsEvent =
  | { name: 'terms_page_view' }
  | { name: 'terms_load_success'; params: { version: string } }
  | { name: 'terms_load_error'; params?: { status?: number } }
  | { name: 'terms_load_retry' }
  | { name: 'terms_modal_open'; params: { document: 'terms' | 'privacy' } }
  | { name: 'terms_modal_accept'; params: { document: 'terms' | 'privacy' } }
  | { name: 'terms_back_click' }
  | { name: 'terms_submit_click'; params: { accepted_marketing: boolean } }
  | { name: 'terms_submit_success'; params: { next_step: string } }
  | { name: 'terms_submit_error' };

const TERMS_EVENTS_WITHOUT_PARAMS = new Set<TermsAnalyticsEvent['name']>([
  'terms_page_view',
  'terms_load_retry',
  'terms_back_click',
  'terms_submit_error',
]);

export type SignupAnalyticsEvent =
  | { name: 'signup_page_view' }
  | { name: 'signup_submit_click' }
  | { name: 'signup_submit_success'; params?: { next_step: string } }
  | { name: 'signup_submit_error' }
  | { name: 'signup_minor_redirect' };

const SIGNUP_EVENTS_WITHOUT_PARAMS = new Set<SignupAnalyticsEvent['name']>([
  'signup_page_view',
  'signup_submit_click',
  'signup_submit_error',
  'signup_minor_redirect',
]);

export type ParentalConsentAnalyticsEvent =
  | { name: 'parental_consent_page_view' }
  | { name: 'parental_consent_guard_redirect' }
  | { name: 'parental_consent_submit_click' }
  | { name: 'parental_consent_submit_success'; params?: { next_step: string } }
  | { name: 'parental_consent_submit_error' }
  | { name: 'parental_consent_back_click' };

const PARENTAL_CONSENT_EVENTS_WITHOUT_PARAMS = new Set<ParentalConsentAnalyticsEvent['name']>([
  'parental_consent_page_view',
  'parental_consent_guard_redirect',
  'parental_consent_submit_click',
  'parental_consent_submit_error',
  'parental_consent_back_click',
]);

export type SuccessAnalyticsEvent =
  | { name: 'success_page_view' }
  | { name: 'success_profile_load_success'; params: { plan: string } }
  | { name: 'success_profile_load_error'; params?: { status?: number } };

const SUCCESS_EVENTS_WITHOUT_PARAMS = new Set<SuccessAnalyticsEvent['name']>([
  'success_page_view',
]);

export type ErrorAnalyticsEvent =
  | {
      name: 'error_page_view';
      params: {
        error_type: ErrorType;
        error_code?: string;
        origin_path?: string;
      };
    }
  | {
      name: 'error_retry_click';
      params: { error_type: ErrorType; error_code?: string };
    }
  | { name: 'error_home_click'; params: { error_type: ErrorType } }
  | { name: 'error_support_click'; params: { error_type: ErrorType } };

const MAX_ERROR_CODE_LENGTH = 100;

function truncateErrorCodeParams<T extends { error_code?: string }>(
  params: T,
): T {
  if (!params.error_code || params.error_code.length <= MAX_ERROR_CODE_LENGTH) {
    return params;
  }

  return {
    ...params,
    error_code: params.error_code.slice(0, MAX_ERROR_CODE_LENGTH),
  };
}

export async function trackMobileEvent(
  event: MobileAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (event.name === 'mobile_page_view') {
      logEvent(analytics, event.name);
    }
  } catch {
    return;
  }
}

export async function trackWelcomeEvent(
  event: WelcomeAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (event.name === 'welcome_page_view') {
      logEvent(analytics, event.name);
      return;
    }

    logEvent(analytics, event.name, event.params);
  } catch {
    return;
  }
}

export async function trackTermsEvent(
  event: TermsAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (TERMS_EVENTS_WITHOUT_PARAMS.has(event.name)) {
      logEvent(analytics, event.name);
      return;
    }

    logEvent(analytics, event.name, 'params' in event ? event.params : undefined);
  } catch {
    return;
  }
}

export async function trackSignupEvent(
  event: SignupAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (SIGNUP_EVENTS_WITHOUT_PARAMS.has(event.name)) {
      logEvent(analytics, event.name);
      return;
    }

    logEvent(analytics, event.name, 'params' in event ? event.params : undefined);
  } catch {
    return;
  }
}

export async function trackParentalConsentEvent(
  event: ParentalConsentAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (PARENTAL_CONSENT_EVENTS_WITHOUT_PARAMS.has(event.name)) {
      logEvent(analytics, event.name);
      return;
    }

    logEvent(analytics, event.name, 'params' in event ? event.params : undefined);
  } catch {
    return;
  }
}

export async function trackSuccessEvent(
  event: SuccessAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    if (SUCCESS_EVENTS_WITHOUT_PARAMS.has(event.name)) {
      logEvent(analytics, event.name);
      return;
    }

    logEvent(analytics, event.name, 'params' in event ? event.params : undefined);
  } catch {
    return;
  }
}

export async function trackErrorEvent(
  event: ErrorAnalyticsEvent,
): Promise<void> {
  try {
    const analytics = await initAnalytics();
    if (!analytics) return;

    const params =
      'error_code' in event.params && event.params.error_code !== undefined
        ? truncateErrorCodeParams(event.params)
        : event.params;

    logEvent(analytics, event.name, params);
  } catch {
    return;
  }
}
